import { Backend } from './Backend';
import { AccessibilityTypes } from '../Business/AccessibilityTypes';
import {
  ICategoryV5,
  IChannelLineupV5,
  IChannelRightsV5,
  IChannelV5,
  IProgramDetailV5,
  IProgramLineupV5,
  IRibbon2Container,
  IScheduleDetailV5,
  ISeriesDetailV5,
  ISeriesLineupV5,
} from './models/AndromeTV.types';
import { ExtraDataType, SortBy, SortDir } from './models/Shared.types';
import { Environment } from '../config/environment';

export class EpgApiV5 {
  /**
   * Request all channels
   */
  static channels(backend: Backend): Promise<IChannelV5[]> {
    const url = backend.makeUrl('epg/v5/channel');
    return backend.getJson(url, backend.bearer) as Promise<IChannelV5[]>;
  }

  static channelRights(backend: Backend): Promise<IChannelRightsV5[]> {
    const query = {
      type: Environment.system,
      entitlementId: backend.sessionInfo?.device.entitlementId,
    };
    const url = backend.makeUrl('epg/v5/channel-right', query);

    return backend.getJson(url, backend.bearer) as Promise<IChannelRightsV5[]>;
  }

  static channelFilter(
    backend: Backend,
    from: Date,
    until: Date,
    categories: string[] = [],
    accessibility?: AccessibilityTypes
  ): Promise<string[]> {
    const queryParams = {
      adult: false,
      categoryTag: [...categories],
      accessibilityOption: accessibility,
      catalogId: backend.deviceProfile?.profile?.catalog,
      from: from.getTime() / 1000,
      until: until.getTime() / 1000,
    };

    const url = backend.makeUrl('epg/v5/channel-filter', queryParams);

    return backend.getJson(url, backend.bearer) as Promise<string[]>;
  }

  static channelLineupRibbon(
    backend: Backend,
    channelId: string,
    from: Date,
    until: Date,
    categories: string[] = [],
    accessibility?: AccessibilityTypes,
    sortDir: SortDir = SortDir.Asc,
    extraData: ExtraDataType = ExtraDataType.NONE
  ) {
    const queryParams = {
      adult: false,
      categoryTag: [...categories],
      accessibilityOption: accessibility,
      catalogId: backend.deviceProfile?.profile?.catalog,
      from: from.getTime() / 1000,
      until: until.getTime() / 1000,
      sortBy: SortBy.schedule,
      sortDir,
      extraData,
    };

    const url = backend.makeUrl(`epg/v5/cms/channel/${channelId}/lineup`, queryParams);

    return backend.getJson(url, backend.bearer) as Promise<IRibbon2Container>;
  }

  static channelLineup(backend: Backend, channelId: string, from: Date, until: Date) {
    const queryParams = {
      channelId,
      // catalogId: backend.deviceProfile?.profile?.catalog, // not needed since we pass channelId
      from: from.getTime() / 1000,
      until: until.getTime() / 1000,
    };

    const url = backend.makeUrl('epg/v5/lineup', queryParams);

    return backend.getJson(url, backend.bearer) as Promise<IChannelLineupV5>;
  }

  static categories(backend: Backend): Promise<ICategoryV5[]> {
    const url = backend.makeUrl('epg/v5/category');
    return backend.getJson(url, backend.bearer) as Promise<ICategoryV5[]>;
  }

  static schedule(backend: Backend, scheduleId: string): Promise<IScheduleDetailV5> {
    const url = backend.makeUrl(`epg/v5/schedule/${scheduleId}`);
    return backend.getJson(url, backend.bearer) as Promise<IScheduleDetailV5>;
  }

  static program(backend: Backend, id: string): Promise<IProgramDetailV5> {
    const url = backend.makeUrl(`epg/v5/program/${id}`, {});

    return backend.getJson(url, backend.bearer) as Promise<IProgramDetailV5>;
  }

  /**
   * channelId Currently used nowhere
   */
  static programLineup(backend: Backend, id: string, from: Date, until: Date, channelId?: string) {
    const query = {
      from: from.getTime() / 1000,
      until: until.getTime() / 1000,
      catalogId: backend.deviceProfile?.profile.catalog,
      channelId,
    };
    const url = backend.makeUrl(`epg/v5/program/${id}/lineup`, query);

    return backend.getJson(url, backend.bearer) as Promise<IProgramLineupV5>;
  }

  static series(backend: Backend, id: string): Promise<ISeriesDetailV5> {
    const url = backend.makeUrl(`epg/v5/series/${id}`, {});

    return backend.getJson(url, backend.bearer) as Promise<ISeriesDetailV5>;
  }

  static seriesLineup(backend: Backend, id: string, from: Date, until: Date, channelId?: string) {
    const query = {
      from: Math.floor(from.getTime() / 1000),
      until: Math.floor(until.getTime() / 1000),
      catalogId: backend.deviceProfile?.profile.catalog,
      channelId,
    };
    const url = backend.makeUrl(`epg/v5/series/${id}/lineup`, query);

    return backend.getJson(url, backend.bearer) as Promise<ISeriesLineupV5>;
  }
}
